import {
  withCompInfo,
  createComponentMapperModel,
} from '@wix/editor-elements-integrations';
import type {
  RatingsInputCssCarmiData,
  RatingsInputPropsCarmiData,
} from '../../RatingsInput/RatingsInput.types';

import type {
  RatingsDisplayDefinition,
  RatingsDisplayPlatformProps,
  RatingsDisplayCSSVars,
  RatingsDisplayProperties,
  RatingsDisplayTranslationKeys,
} from '../RatingsDisplay.types';
import { Translation } from '../constants';
import type {
  Direction,
  PhysicalAlignment,
} from '@wix/editor-elements-common-utils';
import {
  castToInheritIfNeeded,
  convertPhysicalAlignmentToLogical,
  withFallbackValues,
  withSiteDirection,
} from '@wix/editor-elements-common-utils';

export const props = withCompInfo<
  RatingsDisplayPlatformProps,
  RatingsDisplayDefinition,
  RatingsInputPropsCarmiData
>()(
  ['compData', 'compProps', 'translate'],
  ({ compData, compProps, translate }, carmiProps) => {
    const translations = {} as RatingsDisplayPlatformProps['translations'];
    Object.entries(Translation).forEach(([key, value]) => {
      translations[key as RatingsDisplayTranslationKeys] =
        translate(value.namespace, value.key) || value.default;
    });

    return {
      ...carmiProps,
      noReviewsMode: compProps.noReviewsMode,
      rating: compData.rating,
      noReviewsPlaceholder: compData.noReviewsPlaceholder,
      numRatings: compData.numRatings,
      reviewsCountLabel: compData.reviewsCountLabel,
      showReviewsCount: compProps.showReviewsCount,
      showRating: compProps.showRating,
      translations,
    };
  },
);

const getMargins = (
  compProps: RatingsDisplayProperties,
  positionAfter: boolean,
) => {
  const displayLabelsSpacing = `${Math.max(compProps.shapeSpacing, 15)}px`;
  const cssVars: Record<string, string> = {};

  if (compProps.showRating) {
    const ratingMargin = `--ratingMarginInline${
      positionAfter ? 'Start' : 'End'
    }`;

    cssVars[ratingMargin] = displayLabelsSpacing;
  }

  if (compProps.showReviewsCount) {
    cssVars[`--numReviewsMarginInlineStart`] = positionAfter
      ? '15px'
      : displayLabelsSpacing;
  }

  return cssVars;
};
export const css = withCompInfo<
  RatingsDisplayCSSVars,
  RatingsDisplayDefinition,
  RatingsInputCssCarmiData
>()(
  ['compData', 'compProps', 'styleProperties'],
  ({ compData, compProps, styleProperties }, carmiCss) => {
    const {
      shapeSize,
      shapeSpacing,
      ratingPosition,
      showRating,
      showReviewsCount,
    } = compProps;
    const { align } = styleProperties;
    const {
      direction = 'inherit',
      labelDirection = 'inherit',
      ratingsDirection = 'inherit',
    } = compData;
    const positionAfter = ratingPosition === 'after';

    const cssVars: RatingsDisplayCSSVars = {
      ...carmiCss,
      '--iconsMinWidth': `${4 * shapeSpacing + 5 * shapeSize}px`,
      '--iconsOrder': positionAfter ? 0 : 2,
      '--iconSize': `${shapeSize}px`,
      '--iconPaddingEnd': `${shapeSpacing}px`,
      '--halfIconWidth': `${shapeSize / 2}px`,
      '--alignment': align,
      '--direction': withSiteDirection(direction),
      '--label-direction': labelDirection,
      '--ratings-direction': ratingsDirection,
    };

    if (compData.rating && (showRating || showReviewsCount)) {
      return {
        ...cssVars,
        ...getMargins(compProps, positionAfter),
      };
    }

    return cssVars;
  },
  [
    withFallbackValues([
      {
        sourceNamespace: 'compProps',
        targetNamespace: 'compData',
        fields: [{ source: 'direction', target: 'direction' }],
        enhancer: (direction: Direction) =>
          castToInheritIfNeeded(direction, 'ltr'),
      },
      {
        sourceNamespace: 'compProps',
        targetNamespace: 'styleProperties',
        fields: [{ source: 'alignment', target: 'align' }],
        enhancer: (alignment: PhysicalAlignment, { compProps }) =>
          convertPhysicalAlignmentToLogical(alignment, compProps.direction),
      },
    ]),
  ],
);

export default createComponentMapperModel({ props, css });
